class EndPoints {
  BASE_URL = `${process.env.REACT_APP_BASE_URL}api/`;
  NOTIFICATION_BASE_URL = `${process.env.REACT_APP_NOTIFICATION_SERVICE_BASE_URL}`;
  AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL;
  CRM_BASE_URL = `${process.env.REACT_APP_CRM_SERVICE_BASE_URL || "https://devapi.guesthives.com/"}`;

  CRM = {
    GUEST_LIST: "guests",
    GUEST_FILTER_OPTIONS: "guests/filter-options",
    GUEST_CONFLICTS: "guests/conflicts",
    GUEST_COMPARE: "guests/compare",
    GUEST_MERGE: "guests/merge",
    GUEST_DETAIL: (id) => `guests/${id}`,
    GUEST_MASTER_LOCATIONS: (id) => `guests/${id}/master-locations`,
    GUEST_STAYS: (id) => `guests/${id}/stays`,
    GUEST_RESOLVE_CONFLICT: (id) => `guests/${id}/resolve-conflict`,
    GUEST_PREFERENCES: (id) => `guests/${id}/preferences`,
    GUEST_PREFERENCES_ROOM_SERVICE: (id) => `guests/${id}/preferences/room-service`,
    GUEST_PREFERENCES_SPECIAL_CATEGORY: (id) => `guests/${id}/preferences/special-category`,
    GUEST_COMMUNICATION: (id) => `guests/${id}/communication`,
    GUEST_DOCUMENTS: (id) => `guests/${id}/documents`,
    GUEST_PAYMENTS: (id) => `guests/${id}/payments`,
    HOTEL_LIST: "hotels",
    OTA_IN_HOUSE_GUESTS: "ota/in-house-guests",
    OTA_GUEST_LIST: "ota/guest-list",
    OTA_GUEST_LIST_SUMMARY: "ota/guest-list-summary",
    OTA_SOURCE_PERFORMANCE: "ota/source-performance",
  };

  AUTH = {
    LOGIN: "auth/login",
    LOGOUT: "auth/logout",
    FORGET_PASSWORD: "auth/forgetpassword",
    HOTEL_REGISTRATION: "auth/hotelRegister",
    USER_REGISTRATION: "auth/userRegister",
    REFRESH_TOKEN: "auth/refreshtoken",
    CHANGE_PASSWORD: "auth/change-password",
    SWITCH_HOTEL: "auth/switch-hotel",
  };

  USER_MANAGEMENT = {
    CREATE: "user/create",
    LIST: "user/list",
    // POST variant: filters travel in the body so status/search/role always
    // reach the server (the GET wrapper's 2nd arg is params, which is easy to
    // miss) and long search terms cannot overflow the URL.
    SEARCH: "user/list",
    GET: (k) => `user/${k}`,
    UPDATE: (k) => `user/${k}`,
    DEACTIVATE: (k) => `user/${k}/deactivate`,
    ACTIVATE: (k) => `user/${k}/activate`,
    RESET_PASSWORD: (k) => `user/${k}/reset-password`,
  };

  ROLE_MANAGEMENT = {
    LIST: "role/list",
    SEARCH: "role/list",
    ASSIGNABLE: "role/assignable",
    GET: (k) => `role/${k}`,
    CREATE: "role/create",
    UPDATE: (k) => `role/${k}`,
    DEACTIVATE: (k) => `role/${k}/deactivate`,
    ACTIVATE: (k) => `role/${k}/activate`,
  };

  APPROVAL = {
    CONFIG_LIST: "approval/config/list",
    TRANSACTION_TYPE_LIST: "approval/transaction-type/list",
    TRANSACTION_TYPE_SAVE: "approval/transaction-type",
    TRANSACTION_TYPE_GET: (typeKey) => `approval/transaction-type/${typeKey}`,
    TRANSACTION_TYPE_DEACTIVATE: (typeKey) =>
      `approval/transaction-type/${typeKey}/deactivate`,
    TRANSACTION_TYPE_ACTIVATE: (typeKey) =>
      `approval/transaction-type/${typeKey}/activate`,
    QUEUE: "approval/queue",
    QUEUE_COUNT: "approval/queue/count",
    INSTANCE: (instanceKey) => `approval/instance/${instanceKey}`,
    APPROVE: (instanceKey) => `approval/instance/${instanceKey}/approve`,
    REJECT: (instanceKey) => `approval/instance/${instanceKey}/reject`,
    RETRY: (instanceKey) => `approval/instance/${instanceKey}/retry`,
    HISTORY: "approval/history",
    PENDING_MAP: "approval/pending-map",
    ELIGIBLE_APPROVERS: "approval/approvers/eligible",
    APPROVER_TRANSACTION_TYPES: "approval/approver-transaction-types",
    ASSIGN_APPROVERS: "approval/approvers/assign",
    APPROVER_TYPES: "approval/approvers/types",
  };

  NOTIFICATION_PREFERENCE = {
    CATALOG: "notification-preferences/catalog",
    USER: (userKey) => `notification-preferences/user/${userKey}`,
    UPDATE_USER: (userKey) => `notification-preferences/user/${userKey}`,
  };

  PERMISSION_EVENT = {
    LIST: "permission-events/list",
    SEARCH: "permission-events/list",
  };

  // Hotels as the SaaS service knows them. The hms-api /hotel list is the
  // property-local record and must not be used for cross-hotel assignment.
  SAAS_HOTEL = {
    LIST: "hotel/list",
  };

  PERMISSION_ADMIN = {
    REGISTRY: "permissions/registry",
    TEMPLATE: (k) => `permissions/template/${k}`,
    UPDATE_TEMPLATE: (k) => `permissions/template/${k}`,
    // Per-user overrides. These edit user_permissions directly, so they diverge
    // from the role template until the user is reset back to it.
    USER: (k) => `permissions/user/${k}`,
    UPDATE_USER: (k) => `permissions/user/${k}`,
    RESET_USER: (k) => `permissions/user/${k}/reset`,
  };

  GUEST = {
    VERIFY_BOOKING: "guest/verifybooking",
    VERIFY_DRAFT: "guest/verifydraft",
    GET_BOOKING_DETAILS: "guest/getbookingdetails",
    CREATE_SELF_CHECK_IN: "guest/createselfcheckin",
    UPDATE_CUSTOMER_INFO: "guest/updatecustomerinfo",
    GET_ALL_COUNTRIES: "guest/country",
    GET_ALL_STATES: "guest/getallstates",
    GET_COUNTRY_STATE_BY_CITY: "guest/getcountrystatebycity",
    GET_ALL_OCCUPATION: "guest/getalloccupation",
    GET_ALL_PURPOSE_OF_VISIT: "guest/getallpurposeofvisit",
    GET_ALL_CITIES: "guest/getallcities",
    GET_STATE: "guest/state",
    GET_CITY: "guest/city",
    SCAN_IMAGE: "guest/scanOcr",
    GET_ALL_PROOF_TYPES: "guest/getallprooftypes",
  };

  COMPANY = {
    CREATE_TRIAL: "company/create-trial",
  };

  TRIAL = {
    UPGRADE: "trial/upgrade",
    UPDATE: "trial",
  };

  CITY = {
    BY_NAME: "city/by-name",
  };

  GET_HEALTH_CHECK = "health";

  USER = {
    GET_USER_BY_ROLE: "user/usersByRoleId",
    GET_ALL_USERS: "user/getAllUsers",
    GET_ALL_USERS_BY_COMPANY_ID: "user/getAllUsersByCompanyId",
    UPDATE_USER: "/user",
    GET_USER_BY_KEY: "/user",
    GET_USER_BY_FILTER: "user/getuserbyfilter",
    VERIFY: "user/verify",
  };

  BOOKING_ENGINE_CONFIG = {
    GET_CONFIG: "booking-engine-config",
    SAVE_CONFIG: "booking-engine-config/save",
    PUBLISH_CONFIG: "booking-engine-config/publish",
  };

  HOTEL = {
    GET_ALL_HOTEL: "hotel",
    GET_HOTEL_BY_ID: "hotel/gethotelbyid",
    GET_HOTELS_BY_USER: "hotel/gethotelsbyuser",
    GET_HOTEL_CONGIG: "hotel/gethotelconfig",
  };

  ROLE = {
    GET_ALL_ROLES: "role",
  };

  FLOOR = {
    GET_ALL_FLOORS: "floor/getallfloorswithrooms",
  };

  BED = {
    GET_ALL_BED_TYPES: "bed",
    ADD_NEW_BED: "bed",
  };

  ROOM_TYPE = {
    ADD_ROOM_TYPE: "room/addRoomType",
    GET_ALL_ROOM_TYPE: "room/getAllRoomType",
    DELETE_ROOM_TYPE: "room/deleteroomtype",
    UPDATE_ROOM_TYPE: "/room/updateroomtype",
    GET_ROOM_TYPE_BY_KEY: "room/getroomtypebykey",
  };

  UTILS = {
    GET_ALL_AMENITIES: "utils/getallamenities",
    GET_ALL_STAY_TYPES: "utils/getallstaytypes",
    GET_ALL_COUNTRIES: "utils/country",
    GET_ALL_STATES: "utils/getallstates",
    GET_ALL_OCCUPATION: "utils/getalloccupation",
    GET_ALL_PURPOSE_OF_VISIT: "utils/getallpurposeofvisit",
    GET_ALL_CITIES: "utils/getallcities",
    GET_STATE: "/utils/state",
    GET_CITY: "/utils/city",
    GET_ALL_PROOF_TYPES: "utils/getallprooftypes",
  };

  ROOM = {
    ADD_OR_UPDATE_ROOMS: "room/addOrUpdateRooms",
    UPDATE_ROOM: "room/updateRoom",
    DELETE_ROOM: "room/deleteRoom",
    GET_AVAILABLE_ROOMS: "room/getavailablerooms",
    GET_AVAILABLE_ROOMS_TO_RESERVE: "room/getavailableroomstoreserve",
    GET_ROOMS_FOR_SHIFTING: "room/getRoomsForShifting",
    GET_ALL_ROOMS_BY_ROOMTYPE: "room/getallroomsbyroomtype",
    CHECK_OUT: "/booking/checkout",
    ROOM_SHIFT: "room/roomshift",
    RESERVATION_ROOM_SHIFT: "room/reservationroomshift",
    DELETE_BOOKING: "booking/deleteBooking",
    GET_ROOM_OCCUPANCY_BY_ROOM_TYPE: "room/getRoomOccupancyByRoomType",
    GET_OCCUPIED_ROOMS: "room/getallroomsoccupiedstatus",
    GET_ROOM_TYPE_BY_YEAR: "room/getRoomTypeByYear",
    UPDATE_ROOM_MAINTENANCE: "room/updateRoomMaintenance",
    GET_ROOM_BY_ROOM_KEY: "room/getroombyroomkey",
    GET_ROOM_HISTORY_BY_OCCUPANCY: "room/getroomhistorybyoccupancy",
    GET_ALL_ROOM_SHIFT_REASON: "room/getallroomshiftreason",
    GET_BOOKED_ROOMS_BY_FILTER: "room/getbookedroomsbyfilter",
    ASSIGN_ROOM_TO_RESERVE: "room/assignroomtoreserve",
    ASSIGN_ROOM_SELF_CHECK_IN: "room/assignRoomselfcheckin",
    GET_ALL_ROOM_IN_HOTEL: "room/getallrooms",
  };

  ENQUIRY = {
    GET_ALL_ENQUIRY_SOURCES: "enquiry/getallenquirysource",
    GET_ENQUIRES_BY_MOBILE: "enquiry/getenquiriesbymobile",
    GET_ALL_OTA: "enquiry/getallota",
  };

  RESERVATION = {
    CREATE_RESERVATION: "booking/createreservation",
    UPDATE_RESERVATION: "booking/updateallreservations",
    GET_ALL_RESERVATION: "booking/getallreservation",
    CANCEL_RESERVATION: "booking/cancelbookingorreservation",
    GET_ALL_RESERVATION_STATUS: "/booking/getallreservationstatus",
    EXTEND_RESERVATION: "booking/extendreservation",
  };

  GUESTINFO = {
    GET_ALL: "booking",
    GET_ALL_GUEST_HISTORY: "booking/getguesthistorybyfilter",
    GET_ALL_DRAFT: "/booking/getalldraftbyfilter",
    GENERATE_PROVISIONAL_BILL: "provisional-bill/generate",
  };

  CUSTOMER = {
    BLOCK_OR_UNBLOCK_CUSTOMER: "/customer/blockorunblockcustomer",
    GET_CUSTOMERINFO_BY_NUMBER: "/customer",
    GET_ALL_BLACKLIST: "/customer/getallblacklistbyfilter",
  };

  OCR = {
    SCAN_IMAGE: "utils/scanOcr",
    VALIDATE_PDF_PAGES: "utils/validatePdfPages",
  };

  BOOKING = {
    GET_DRAFT_BY_ID: "booking/draft",
    ADD_OR_UPDATE_DRAFT: "booking/addorupdatedraft",
    DELETE_DRAFT_BY_ID: "booking/draft",
    GET_ALL_CHECKIN_CHECKOUT: "booking/getallcheckincheckout",
    GET_ALL_CHECKOUT: "booking/getAllCheckOut",
    GET_ALL_EXPECTED_ARRIVAL: "booking/getallexpectedarrival",
    GET_ALL_EXPECTED_DEPARTURE: "booking/getallexpecteddeparture",
    RESTORE_BOOKING: "booking/restore",
    EXTEND_STAY: "booking/extendStay",
    GET_ALL_PENDING_DUES: "payment/getallpaymentdues",
    GET_ROOMBOOKING_BY_ROOMBOOKING_ID: "booking/roombooking",
    GET_ALL_BOOKING: "booking/getallbooking",
    GENERATE_SELF_CHECK_IN_URL: "booking/generateSelfCheckInUrl",
    GENERATE_SELF_UPDATE_URL: "booking/generateselfupdateurl",
  };

  OCCUPANCY = {
    CREATE_OCCUPANCY: "occupancy/createoccupancy",
    UPDATE_ALL_OCCUPANCY: "occupancy/updatealloccupancies",
    CHECK_OUT_OCCUPANCIES: "occupancy/checkoutoccupancies",
    UPDATE_CHECK_OUT: "occupancy/updatecheckout",
    VALIDATE_REFERRAL: "occupancy/validatereferral",
    DELETE_OCCUPANCY: "occupancy/deleteoccupancybyfilter",
    EXTEND_OCCUPANCY: "occupancy/extendoccupancy",
    UNDO_DEPARTURE: "occupancy/undodeparture",
    GET_OCCUPANCY_DAY_RENT: "occupancy/getoccupancydaytrent",
    MOVE_TO_CHECK_IN: "occupancy/movetocheckin",
  };

  CLEANING_STATUS = {
    GET_ALL_CLEANING_STATUS: "status/getCleaningStatus",
  };

  HOUSE_KEEPING = {
    GET_ROOM_CLEANING_STATUS: "housekeeping/getroomcleaningstatus",
    CHANGE_ROOM_CLEANING_STATUS: "housekeeping/changeRoomCleaningStatus",
    GET_ALL_CLEANING_STATUS: "housekeeping/getAllCleaningStatus",
    MOVE_ROOMS_TO_DIRTY: "housekeeping/moveroomstodirty",
    GET_ALL_HOUSE_KEEPERS: "housekeeping/gethousekeepers",
    ADD_HOUSE_KEEPER: "housekeeping/newHouseKeeper",
    DELETE_HOUSE_KEEPER: "housekeeping/deleteHouseKeeper",
    UPDATE_HOUSE_KEEPER: "housekeeping/updateHouseKeeper",
  };

  STATUS = {
    GET_ALL_CLEANING_STATUS: "status/getCleaningStatus",
    GET_ALL_CANCELLATION_REASON: "status/getallcancellationreason",
  };

  PAYMENT = {
    CREATE_PAYMENT: "payment/createpayment",
    GET_PAYMENT_HISTORY: "/payment/getpaymenthistorybybooking/",
    GET_PAYMENT_DETAIL_BY_BOOKING: "/payment/getpaymentdetailbybooking/",
    GET_ALL_PAYMENT_TYPE: "/payment/getAllPaymentType",
    UPDATE_PAYMENT_HISTORY: "payment/updatepaymenthistory/",
    DELETE_PAYMENT_HISTORY: "payment/deletepayment",
    BATCH_UPDATE_PAYMENTS: "payment/batchupdatepayments",
  };

  REFUND = {
    CREATE: "refund/create",
    HISTORY: (booking_id) => `refund/history/${booking_id}`,
    BY_KEY: (refund_key) => `refund/${refund_key}`,
    CANCEL: (refund_key) => `refund/${refund_key}/cancel`,
    RECEIPT: (refund_key) => `refund/receipt/${refund_key}`,
    REPORT: "refund/report",
  };

  TAX = {
    CALCULATE_TAX: "tax/calculateTax",
    GET_TAX_RATE_SLABS: "tax/getalltaxrateslabs",
    UPDATE_ROOM_PRICE_AND_TAXES: "/tax/updateroompriceandtaxes",
  };

  INVOICE = {
    GET_INVOICE: "/invoice/getinvoicebyfilter",
    GET_INVOICE_BY_KEY: "invoice/getinvoicebykey",
    GET_ALL_INVOICE_TYPE: "invoice/getallinvoicetype",
    UNDO_OR_CANCEL_INVOICE: (invoice_key) => `invoice/undoOrCancelInvoice/${invoice_key}`,
  };

  ADDON = {
    GET_EXTRA_PERSON_HISTORY: "addon/getextrapersonhistorybyoccupancy",
    ADD_EXTRA_PERSON: "addon/addpersons",
    UPDATE_EXTRA_PERSON: "addon/updateAddons",
    DELETE_ADDON: "addon/deleteaddon",
  };

  SALES_REPORT = {
    GET_ALL: "report/getsalesreport",
  };

  GST_REPORT = {
    GET_ALL: "report/getgstreport",
  };

  FOREIGNER_REPORT = {
    GET_ALL: "report/getforeignersreport",
  };
  OCCUPANCY_REPORT = {
    GET_ALL: "report/getoccupancyreport",
  };
  CANCELLATION_REPORT = {
    GET_ALL: "report/getcancellationreport",
  };
  COLLECTION_REPORT = {
    GET_ALL: "report/getdailycollectionreport",
  };
  HOUSEKEEPING_REPORT = {
    GET_ALL: "report/gethousekeepingreport",
  };

  NIGHT_AUDIT_REPORT = {
    GENERATE_REPORT: "night_audit/generate",
    GET_REPORT_BY_DATE: "night_audit/report"
  };

  NIGHT_AUDIT_OPERATIONAL_REPORT = {
    GET_REPORT_BY_DATE: "night_audit/operational-report"
  };

  NIGHT_AUDIT_PROCESS = {
    GET_RESERVATION_AUDIT: "night_audit/reservation-audit",
    GET_IN_HOUSE_AUDIT: "night_audit/in-house-audit",
    GET_FOLIO_AUDIT: "night_audit/folio-audit",
    VALIDATE_STEP: "night_audit/validate-step",
    PERFORM_ACTION: "night_audit/action",
    COMPLETE_AUDIT: "night_audit/complete"
  };

  GUEST_REPORT = {
    GET_REPORT_BY_FILTER: "report/getguestreport"
  };

  NOTIFICATION = {
    GET_ALL_NOTIFICATION_SETTINGS: "notification/get-all-notification-settings",
    UPDATE_NOTIFICATION_SETTINGS: "notification/update-notification-settings",
    GET_ALL_NOTIFICATIONS: "notification/get-all-notifications",
    UPSERT_DEVICE_TOKENS: "notification/upsert-fcm-token",
    MARK_AS_READ: "notification/mark-as-read",
  };

  ASSET = {
    GET_ALL_STATUS: "asset/getAssetStatusList",
    GET_ALL_CATEGORIES: "asset/getAssetCategories",
    GET_ALL_ASSETS: "asset/getAllAssets",
    ADD_ASSET: "asset/addAsset",
    ADD_NEW_CATEGORY: "asset/addAssetCategory",
    DELETE_ASSET: "asset/deleteAsset",
  };

  EMPLOYEE = {
    GET_ALL_MARITAL_STATUS: "employee/getallmaritalstatus",
    GET_ALL_DEPARTMENT: "employee/getalldepartment",
    GET_ALL_DESIGNATION: "employee/getalldesignation",
    GET_ALL_WORK_TYPE: "employee/getallworktype",
    GET_ALL_EMPLOYEES: "employee/getallemployees",
    GET_ALL_EDUCATION_LEVELS: "employee/getalleducationlevel",
    CREATE_EMPLOYEE: "employee/createemployee",
    UPDATE_EMPLOYEE: "employee/updateemployee",
    DELETE_EMPLOYEE: "employee/deleteemployee",
    CREATE_EMPLOYEE_TASK: "employee/task",
    GET_ALL_TASKS: "employee/task",
    UPDATE_TASK_STATUS: "employee/task",
    CREATE_SHIFT: "employee/createshift",
    GET_ALL_SHIFTS: "employee/shifts",
    DELETE_SHIFT: "employee/shift",
    CREATE_SHIFT_ASSIGNMENT: "employee/shift-assignment",
    ATTENDANCE_TODAY: "employee/attendance-today",
    GET_SHIFT_ASSIGNMENTS: "employee/shift-assignments",
    DELETE_SHIFT_ASSIGNMENT: "employee/shift-assignment",
    CREATE_ATTENDANCE: "employee/attendance",
    GET_ATTENDANCE_RECORDS: "employee/attendance-records",
    APPLY_LEAVE: "employee/apply-leave",
  };

  ADMIN_PANEL = {
    GET_OCCUPANCY_RATES: "adminpanel/getoccupancyrates",
    GET_REVENUES: "adminpanel/getRevenues",
    GET_NOSHOW_AND_CANCELLATION: "adminpanel/getnoshowandcancellations",
    GET_BOOKING_SOURCE_DATA: "adminpanel/getbookingsourcedata",
    GET_ALL_CHECKIN_OR_CHECKOUT: "adminpanel/getallcheckinorcheckout",
    GET_REVENUE_By_COMPANY: "adminpanel/getrevenuebycompany",
  };

  MOCK = {
    GENERATE: "/mock/generate",
  };

  GOOGLE = {
    AUTH_URL: "google/auth-url",
    EMAILS: "google/emails",
    SEND_MAIL: "google/send",
  };

  REVENUE = {
    CREATE_PRICING_RULE: "revenue/createpricingrule",
    UPDATE_PRICING_RULE: "revenue/pricingrule",
    PATCH_PRICING_RULE: "revenue/pricingrule",
    DELETE_PRICING_RULE: "revenue/pricingrule",
    GET_ALL_PRICING_RULES: "revenue/pricingrules",
    GET_PRICING_RULE: "revenue/pricingrule",
    GET_PRICING_RULE_BY_ROOM_TYPE: "revenue/pricingrulebyroomtype",
    GET_PRICING_RULE_COUNT: "revenue/pricingrulecount",
    CHECK_RULE_USAGE: "revenue/checkruleusage",
  };

  EVENT = {
    CREATE_EVENT_CATEGORY: "event/create-category",
    GET_EVENT_CATEGORIES: "event/event-categories",
  };

  WHATSAPP = {
    GET_ALL_CHATS: "message/chats",
    GET_CUSTOMER_MESSAGES: "message/messages",
    MARK_AS_READ: "message/markasread",
    UPLOAD_MEDIA: "message/uploadmedia",
    UPDATE_NAME: "message/updatename",
    DELETE_MESSAGE: "message/deletemessage",
  };

  CHARGE_POSTING = "chargepost";

  REVENUE_TYPE = "revenuetype";

  OUTLET = "outlet";

  PAID_OUT_TYPES = "paidouttype";

  DISBURSEMENT_TYPES = "disbursementtype";

  OCCUPANCY_CHARGE = "occupancycharge";

  CHANNEL = {
    OTA: "/channel/ota",
    PERFORMANCE: "/channel/performance",
    BOOKING_SOURCE: "/channel/bookingsource",
    BOOKINGS: "/channel/bookings",
    GROWTH: "/channel/growth",
    REVENUE: "/channel/revenue",
    REVENUE_COMPARISON: "/channel/revenuecomparison",
    GET_ALL_CHANNELS: "/channel/getallchannels",
    ROOM_TYPE_BY_OTA: "/channel/roomtypebyota",
    REVENUE_COMPARISON_BY_SINGLE_OTA: "/channel/revenuecomparisonbysingleota",
  };

  CHANNEL_MANAGER = {
    GET_RATES: "channel-manager/getrates",
    UPDATE_RATES: "channel-manager/updaterates",
    PUBLISH_RATES: "channel-manager/publishrates",
    SYNC_STATUS: "channel-manager/syncstatus",
    SYNC_LOGS: "channel-manager/synclogs",
    GET_RESTRICTIONS: "channel-manager/getrestrictions",
    UPDATE_RESTRICTIONS: "channel-manager/updaterestrictions",
    GET_ROOM_COUNTS: "channel-manager/getroomcounts",
    UPDATE_ROOM_COUNTS: "channel-manager/updateroomcounts",
    GET_POTENTIAL_NO_SHOWS: "channel-manager/potential-no-shows",
    MARK_NO_SHOWS: "channel-manager/mark-no-shows",
    GET_CONFIG: "channel-manager/getconfig",
    UPDATE_CONFIG: "channel-manager/updateconfig",
  };

  RATE_PLAN = {
    CREATE: "rate-plan/",
    GET_ALL: "rate-plan/",
    UPDATE: "rate-plan/",
    DELETE: "rate-plan/",
    GET_USAGE: (ratePlanId) => `rate-plan/${ratePlanId}/usage`,
    UPDATE_DEFAULT: "rate-plan/default",
    GET_BY_ROOM_TYPE: "rate-plan/room-type/",
    GET_ROOM_DAYS_PRICE: "rate-plan/get-room-days-price",
  };

  RATE_PLAN_PRICING = {
    GET: "rate-plan-pricing/getrateplanpricing",
    UPDATE: "rate-plan-pricing/updaterateplanpricing",
    CALENDAR: "rate-plan-pricing/getrateplanpricing",
    CALCULATION: "rate-plan-pricing/getpricingcalculation",
  };

  TUTORIAL = {
    GET_ALL_MODULES: "tutorial/modules",
    ADD_MODULE: "tutorial/module",
    UPDATE_MODULE: "tutorial/module",
    DELETE_MODULE: "tutorial/module",
    GET_CONTENT_BY_MODULE: "tutorial/contents",
    ADD_CONTENT: "tutorial/content",
    UPDATE_CONTENT: "tutorial/content",
    DELETE_CONTENT: "tutorial/content",
    GET_CONTENT_BY_ID: "tutorial/content/get-by-id",
  };

  REVIEW = {
    SUBMIT: "review/submit",
    SEND_EMAIL: "review/send-email",
    LIST: "review/list",
  };

  COUPON = {
    LIST: "coupon/list",
    CREATE: "coupon/create",
    UPDATE: (key) => `coupon/${key}`,
    DELETE: (key) => `coupon/${key}`,
    TEMPLATES: (category) => `coupon/templates${category ? `?category=${category}` : ""}`,
    GUEST_OFFERS: "coupon/guest-offers",
    GUEST_OFFERS_REPORT: "coupon/getguestoffersreport",
    AVAILABLE: (customer_id) => `coupon/available/${customer_id}`,
    VALIDATE_CODE: "coupon/validate-code",
    REDEEM: "coupon/redeem",
    TEMPLATE_BY_ID: (id) => `coupon/templates/${id}`,
  };

  LAUNDRY_CONFIG = {
    GET: "laundry/config",
    UPDATE: "laundry/config",
  };

  LAUNDRY_MASTER = {
    GET_CATEGORIES: "laundry/master/categories",
    CREATE_CATEGORY: "laundry/master/categories",
    UPDATE_CATEGORY: (id) => `laundry/master/categories/${id}`,
    DELETE_CATEGORY: (id) => `laundry/master/categories/${id}`,
    GET_ITEMS: "laundry/master/items/list",
    GET_ACTIVE_ITEMS: "laundry/master/items/active",
    GET_ITEM: (id) => `laundry/master/items/${id}`,
    CREATE_ITEM: "laundry/master/items",
    UPDATE_ITEM: (id) => `laundry/master/items/${id}`,
    SET_ITEM_STATUS: (id) => `laundry/master/items/${id}/status`,
    DELETE_ITEM: (id) => `laundry/master/items/${id}`,
    IMPORT_TEMPLATE: "laundry/master/import/template",
    IMPORT_VALIDATE: "laundry/master/import/validate",
    IMPORT_CONFIRM: "laundry/master/import/confirm",
  };

  LAUNDRY_TICKET = {
    STAY_LOOKUP: "laundry/ticket/stay-lookup",
    CREATE: "laundry/ticket",
    CREATE_NON_REVENUE: "laundry/ticket/non-revenue",
    GET_ALL: "laundry/ticket/list",
    GET_COUNTS: "laundry/ticket/counts",
    GET_DELAYED: "laundry/ticket/delayed",
    SCAN: "laundry/ticket/scan",
    GET: (ticketKey) => `laundry/ticket/${ticketKey}`,
    UPDATE: (ticketKey) => `laundry/ticket/${ticketKey}`,
    CANCEL: (ticketKey) => `laundry/ticket/${ticketKey}/cancel`,
    UPDATE_STATUS: (ticketKey) => `laundry/ticket/${ticketKey}/status`,
    HISTORY: (ticketKey) => `laundry/ticket/${ticketKey}/history`,
    TAG: (ticketKey) => `laundry/ticket/${ticketKey}/tag`,
    AUDIT_LOG: "laundry/audit-log",
  };

  LAUNDRY_BILLING = {
    GET: (ticketKey) => `laundry/ticket/${ticketKey}/billing`,
    POST: (ticketKey) => `laundry/ticket/${ticketKey}/billing/post`,
    REVERSE: (ticketKey) => `laundry/ticket/${ticketKey}/billing/reverse`,
    PAY_SEPARATELY: (ticketKey) => `laundry/ticket/${ticketKey}/billing/pay-separately`,
    CONVERT: (ticketKey) => `laundry/ticket/${ticketKey}/billing/convert`,
    DISCOUNT: (ticketKey) => `laundry/ticket/${ticketKey}/billing/discount`,
    CREDIT: (ticketKey) => `laundry/ticket/${ticketKey}/billing/credit`,
    REFUND: (ticketKey) => `laundry/ticket/${ticketKey}/billing/refund`,
    BY_OCCUPANCY: (occupancyId) => `laundry/billing/occupancy/${occupancyId}`,
    PREFLIGHT: "laundry/checkout/preflight",
    SEPARATE_INVOICE: "laundry/billing/separate-invoice",
  };

  LAUNDRY_VENDOR = {
    GET_ALL: "laundry/vendor/list",
    GET_ACTIVE: "laundry/vendor/active",
    CREATE: "laundry/vendor",
    GET: (id) => `laundry/vendor/${id}`,
    UPDATE: (id) => `laundry/vendor/${id}`,
    GET_RATES: (id) => `laundry/vendor/${id}/rates`,
    SAVE_RATES: (id) => `laundry/vendor/${id}/rates`,
    DISPATCHABLE: (id) => `laundry/vendor/${id}/dispatchable`,
  };

  LAUNDRY_VENDOR_INVOICE = {
    GET_ALL: "laundry/vendor/invoice/list",
    RECONCILIATION: "laundry/vendor/invoice/reconciliation",
    SUGGESTED_LINES: "laundry/vendor/invoice/suggested-lines",
    CREATE: "laundry/vendor/invoice",
    GET: (invoiceKey) => `laundry/vendor/invoice/${invoiceKey}`,
    UPDATE: (invoiceKey) => `laundry/vendor/invoice/${invoiceKey}`,
    DELETE: (invoiceKey) => `laundry/vendor/invoice/${invoiceKey}`,
  };

  LAUNDRY_DISPATCH = {
    GET_ALL: "laundry/dispatch/list",
    GET_COUNTS: "laundry/dispatch/counts",
    RECONCILIATION: "laundry/dispatch/reconciliation",
    CREATE: "laundry/dispatch",
    GET: (dispatchKey) => `laundry/dispatch/${dispatchKey}`,
    RECORD_RETURN: (dispatchKey) => `laundry/dispatch/${dispatchKey}/return`,
    CONFIRM_SHORTAGE: (dispatchKey) => `laundry/dispatch/${dispatchKey}/confirm-shortage`,
  };

  LAUNDRY_COMPLAINT = {
    GET_ALL: "laundry/complaint/list",
    GET_COUNTS: "laundry/complaint/counts",
    CREATE: "laundry/complaint",
    GET: (id) => `laundry/complaint/${id}`,
    UPDATE: (id) => `laundry/complaint/${id}`,
    RESOLVE: (id) => `laundry/complaint/${id}/resolve`,
    ADD_PHOTOS: (id) => `laundry/complaint/${id}/photos`,
    DELETE_PHOTO: (id, photoId) => `laundry/complaint/${id}/photos/${photoId}`,
  };

  LAUNDRY_COST_RECOVERY = {
    GET_ALL: "laundry/cost-recovery/list",
    CREATE: "laundry/cost-recovery",
    UPDATE: (id) => `laundry/cost-recovery/${id}`,
  };

  LAUNDRY_REPORT = {
    REVENUE: "laundry/report/revenue",
    REVENUE_DRILLDOWN: "laundry/report/revenue/drilldown",
    TAT: "laundry/report/tat",
    OUTSTANDING: "laundry/report/outstanding",
    VENDOR: "laundry/report/vendor",
    NON_REVENUE: "laundry/report/non-revenue",
  };
}


export const END_POINT = new EndPoints();
